import React from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Calendar, Crown, Sparkles, Clock, CheckCircle2 } from "lucide-react";

/**
 * SubscriptionTimeline — visual timeline of trial/billing milestones.
 * Handles: free, trialing, active (pro), admin, expired tiers.
 */
export default function SubscriptionTimeline() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const sub = user?.subscription || {};
  const isAdmin = user?.role === "admin";

  const cardCls = `rounded-2xl p-5 border ${isDark ? "bg-[hsl(222,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  // ── Admin ──
  if (isAdmin) {
    return (
      <section className={cardCls} data-testid="subscription-timeline">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-bold">{bg ? "Администратор" : "Administrator"}</h3>
        </div>
        <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Имате пълен достъп без лимити." : "You have full unrestricted access."}
        </p>
      </section>
    );
  }

  const now = new Date();

  // Parse key dates defensively.
  const parse = (iso) => {
    if (!iso) return null;
    try { return new Date(iso); } catch { return null; }
  };

  const trialStart = parse(sub.trial_start);
  const trialEnd   = parse(sub.trial_end);
  const periodEnd  = parse(sub.current_period_end || sub.period_end);
  const lastRefill = parse(sub.last_refill_at);

  const daysUntil = (d) => d ? Math.max(0, Math.ceil((d - now) / (1000 * 60 * 60 * 24))) : null;

  const fmt = (d) => {
    if (!d) return "—";
    try {
      return d.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  // ── Milestones array per status ──
  const status = sub.status || "free";
  const milestones = [];

  if (status === "trialing" && trialEnd) {
    const daysLeft = daysUntil(trialEnd);
    milestones.push({
      icon: Sparkles, color: "sky", done: true,
      label_bg: "Започнат пробен период", label_en: "Trial started",
      date: trialStart,
    });
    milestones.push({
      icon: Clock, color: daysLeft <= 3 ? "rose" : daysLeft <= 7 ? "amber" : "emerald", done: false,
      label_bg: `Край на пробния период (${daysLeft} ${daysLeft === 1 ? "ден" : "дни"} остават)`,
      label_en: `Trial ends (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)`,
      date: trialEnd, highlight: true,
    });
    milestones.push({
      icon: Crown, color: "violet", done: false,
      label_bg: "Избор на план (Pro/Clinician)", label_en: "Pick a plan (Pro/Clinician)",
      date: null,
    });
  } else if (status === "active") {
    milestones.push({
      icon: CheckCircle2, color: "emerald", done: true,
      label_bg: `${sub.plan_name_bg || "Pro"} план активен`, label_en: `${sub.plan_name_en || "Pro"} plan active`,
      date: lastRefill || trialStart || null,
    });
    if (periodEnd) {
      milestones.push({
        icon: Calendar, color: "sky", done: false,
        label_bg: "Следваща такса/подновяване", label_en: "Next billing / renewal",
        date: periodEnd, highlight: true,
      });
    }
  } else if (status === "expired") {
    milestones.push({
      icon: Clock, color: "rose", done: true,
      label_bg: "Пробният период приключи", label_en: "Trial expired",
      date: trialEnd || null,
    });
    milestones.push({
      icon: Crown, color: "violet", done: false,
      label_bg: "Активирайте Pro план за достъп", label_en: "Activate Pro to resume",
      date: null, highlight: true,
    });
  } else {
    // free
    milestones.push({
      icon: Sparkles, color: "gray", done: true,
      label_bg: "Безплатен достъп (ограничен)", label_en: "Free access (limited)",
      date: null,
    });
    milestones.push({
      icon: Crown, color: "violet", done: false,
      label_bg: "Стартирайте пробен период за пълен достъп", label_en: "Start trial for full access",
      date: null, highlight: true,
    });
  }

  const colorCls = {
    emerald: "bg-emerald-500 text-white",
    sky:     "bg-sky-500 text-white",
    amber:   "bg-amber-500 text-white",
    rose:    "bg-rose-500 text-white",
    violet:  "bg-violet-500 text-white",
    gray:    isDark ? "bg-white/10 text-gray-400" : "bg-gray-200 text-gray-600",
  };

  return (
    <section className={cardCls} data-testid="subscription-timeline">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
        <h3 className="text-sm font-bold uppercase tracking-wider">
          {bg ? "Времева линия на абонамента" : "Subscription timeline"}
        </h3>
      </div>

      <ol className="relative space-y-4">
        {/* Vertical connector line */}
        <div className={`absolute left-[15px] top-3 bottom-3 w-px
          ${isDark ? "bg-white/10" : "bg-gray-200"}`} aria-hidden />

        {milestones.map((m, i) => {
          const Icon = m.icon;
          return (
            <li key={i} className="relative flex items-start gap-3" data-testid={`subscription-milestone-${i}`}>
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                ring-4 ${isDark ? "ring-[hsl(222,47%,10%)]" : "ring-white"}
                ${m.done ? colorCls[m.color] : (isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400")}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <p className={`text-sm leading-snug ${m.highlight ? "font-semibold" : ""}`}>
                  {bg ? m.label_bg : m.label_en}
                </p>
                {m.date && (
                  <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {fmt(m.date)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
